import { Box, Button, Typography } from "@mui/joy";
import { AlertCircle, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const AUTOSAVE_SECONDS = 5;

type UnsavedChangesBannerProps = {
    open: boolean;
    syncing?: boolean;
    /** Changes whenever edits happen — restarts the autosave countdown. */
    resetKey?: unknown;
    onSave: () => void;
};

export function UnsavedChangesBanner({
    open,
    syncing = false,
    resetKey,
    onSave,
}: UnsavedChangesBannerProps) {
    const [countdown, setCountdown] = useState(AUTOSAVE_SECONDS);
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;

    useEffect(() => {
        if (!open || syncing) {
            return;
        }

        let remaining = AUTOSAVE_SECONDS;
        setCountdown(remaining);

        const intervalId = window.setInterval(() => {
            remaining -= 1;
            if (remaining > 0) {
                setCountdown(remaining);
                return;
            }

            window.clearInterval(intervalId);
            setCountdown(0);
            onSaveRef.current();
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [open, syncing, resetKey]);

    if (!open) {
        return null;
    }

    const title = syncing
        ? "Saving…"
        : countdown > 0
          ? `Autosaving in ${countdown}`
          : "Autosaving…";
    const body = syncing
        ? "Writing your latest estimate changes."
        : "Hang tight — changes will save automatically.";

    return (
        <Box
            sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 80,
                zIndex: 1200,
                display: "flex",
                justifyContent: "center",
                px: 2,
                pointerEvents: "none",
            }}
        >
            <Box
                sx={{
                    pointerEvents: "auto",
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: 1.5,
                    px: 2.5,
                    py: 1.75,
                    width: "100%",
                    maxWidth: 820,
                    minWidth: { sm: 640 },
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "warning.outlinedBorder",
                    bgcolor: "warning.softBg",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        flex: 1,
                    }}
                >
                    <Box
                        sx={{
                            color: "warning.500",
                            bgcolor: "warning.softActiveBg",
                            borderRadius: "50%",
                            width: 32,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: 700,
                            fontSize:
                                !syncing && countdown > 0
                                    ? "0.85rem"
                                    : undefined,
                        }}
                    >
                        {!syncing && countdown > 0 ? (
                            countdown
                        ) : (
                            <AlertCircle size={18} />
                        )}
                    </Box>
                    <Box>
                        <Typography level="title-sm" color="warning">
                            {title}
                        </Typography>
                        <Typography
                            level="body-xs"
                            sx={{ color: "text.secondary" }}
                        >
                            {body}
                        </Typography>
                    </Box>
                </Box>

                <Button
                    size="sm"
                    variant="solid"
                    color="warning"
                    loading={syncing}
                    onClick={onSave}
                    startDecorator={<Save size={16} />}
                    sx={{ borderRadius: "4px", flexShrink: 0, minWidth: 108 }}
                >
                    Save now
                </Button>
            </Box>
        </Box>
    );
}
